import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import {
  submitToHubspot,
  validateField,
} from "../Products/MidstreamContactForm";
import "./HomeV2.scss";
import dashboard1 from "../../assets/midstream-product-images/dashboard-1.png";
import dashboard2 from "../../assets/midstream-product-images/dashboard-2.png";
import dashboard3 from "../../assets/midstream-product-images/dashboard-3.png";

const IMG = `${process.env.PUBLIC_URL}/assets/img/home`;

const NAV_LINKS = [
  { label: "Midstream", to: "/midstream" },
  { label: "Energy Transition", to: "/energy-transition" },
  { label: "Upstream", to: "/upstream" },
  { label: "Insights", to: "/news", disabled: true },
  { label: "Careers", to: "/careers" },
  { label: "Contacts", to: "/contact" },
];

const JOURNEY = [
  { label: "Wellhead", tone: "teal-alt", line: "solid" },
  { label: "Gathering", tone: "teal", line: "solid" },
  { label: "Plant", tone: "teal", line: "gradient" },
  { label: "Pipeline", tone: "amber", line: "gradient" },
  { label: "Settlement", tone: "amber" },
];

const PILLARS = [
  {
    icon: "icon-physics.svg",
    width: 20,
    height: 23,
    title: "Physics behind every number.",
    titleWidth: 284,
    body: "Engineers can inspect and change every assumption.",
  },
  {
    icon: "icon-dollar.svg",
    width: 22,
    height: 22,
    title: "Every decision priced in dollars.",
    titleWidth: 308,
    body: "With the math behind it, ready for the board, a lender, or an auditor.",
  },
  {
    icon: "icon-agents.svg",
    width: 23,
    height: 23,
    title: "Agents that do the legwork.",
    titleWidth: 262,
    body: "They run scenarios against your data and flag what changed.",
  },
];

const PATHS = [
  {
    title: "Midstream.",
    image: "path-midstream.png",
    imageClass: "hv2-shot--crop",
    body: "You're leaving money on the table. We can tell you how much.",
    cta: "See Dsider for Midstream",
    to: "/midstream",
    primary: true,
  },
  {
    title: "Energy transition.",
    image: "path-energy-transition.png",
    body: "Price every design, incentive, and offtake choice before you commit capital.",
    cta: "Explore energy transition →",
    to: "/energy-transition",
  },
  {
    title: "Upstream.",
    image: "path-upstream.png",
    body: "Know the true value of your offshore asset and optimize its production.",
    cta: "Explore upstream →",
    to: "/upstream",
  },
];

const INDUSTRIES = ["Midstream", "Upstream", "Energy transition", "Other"];

const FOOTER_NAV = [
  { label: "Midstream", to: "/midstream" },
  { label: "Energy Transition", to: "/energy-transition" },
  { label: "Upstream", to: "/upstream" },
  { label: "Careers", to: "/careers" },
  { label: "Contacts", to: "/contact" },
];

const emptyForm = {
  firstName: "",
  lastName: "",
  designation: "",
  organization: "",
  email: "",
  industry: INDUSTRIES[0],
};

const REQUIRED = ["firstName", "lastName", "designation", "organization", "email"];

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Stagger index for entrance animations (read as var(--d) in the SCSS)
const d = (i) => ({ "--d": i });

const ArrowIcon = () => (
  <img src={`${IMG}/arrow-right.svg`} alt="" width="13" height="13" />
);

const TopNav = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const barRef = useRef(null);

  // Solid, lifted nav once the page scrolls, plus a reading-progress bar
  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrolled(y > 24);
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      }
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <header className={`hv2-nav${scrolled ? " is-scrolled" : ""}`}>
      <span className="hv2-nav__progress" ref={barRef} aria-hidden="true" />
      <Link to="/" className="hv2-nav__brand" aria-label="Dsider home">
        <img
          className="hv2-logo"
          src={`${IMG}/logo-nav.svg`}
          alt="Dsider"
          width="155"
          height="32"
        />
      </Link>
      <nav
        className={`hv2-nav__links${open ? " is-open" : ""}`}
        aria-label="Primary"
      >
        {NAV_LINKS.map((l) =>
          l.disabled ? (
            <span key={l.label} className="is-disabled" aria-disabled="true">
              {l.label}
            </span>
          ) : (
            <Link
              key={l.label}
              to={l.to}
              className={pathname === l.to ? "is-active" : undefined}
              onClick={() => setOpen(false)}
            >
              {l.label}
            </Link>
          )
        )}
      </nav>
      <div className="hv2-nav__actions">
        <Link to="/#contact" className="hv2-btn hv2-btn--primary hv2-btn--sm">
          Talk to our team
        </Link>
        <button
          type="button"
          className="hv2-nav__toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="hv2-hero">
    <div className="hv2-hero__inner">
      <div className="hv2-hero__copy">
        <span className="hv2-pill hv2-hero-in" style={d(0)}>
          <i aria-hidden="true" />
          Decision intelligence for energy
        </span>
        <h1 className="hv2-hero-in" style={d(1)}>
          Decisions grounded in <em>physics and economics.</em>
        </h1>
        <p className="hv2-hero__sub hv2-hero-in" style={d(2)}>
          Dsider follows the molecule and puts a dollar value on every choice
          along the way, so your team can make the call, show the math, and
          stand behind it.
        </p>
        <div className="hv2-hero__actions hv2-hero-in" style={d(3)}>
          <Link to="/#contact" className="hv2-btn hv2-btn--primary">
            Talk to our team <ArrowIcon />
          </Link>
          <Link to="/midstream" className="hv2-btn hv2-btn--ghost">
            For midstream operators →
          </Link>
        </div>
      </div>
      <div className="hv2-hero__visual hv2-hero-in hv2-from-right" style={d(2)}>
        <img
          src={`${IMG}/hero-midstream.png`}
          alt="Isometric illustration of oil and gas production, refining, offshore and fuel distribution"
        />
      </div>
    </div>
  </section>
);

const SHOWCASE = [
  {
    label: "Midstream",
    title: "Midstream.",
    image: dashboard1,
    text: "You're leaving money on the table. We can tell you how much.",
  },
  {
    label: "Energy transition",
    title: "Energy transition.",
    image: dashboard2,
    text: "Price every design, incentive, and offtake choice before you commit capital.",
  },
  {
    label: "Upstream",
    title: "Upstream.",
    image: dashboard3,
    text: "Know the true value of your offshore asset and optimize its production.",
  },
];


// Three dashboard views. On tablet/desktop the strip pins below the nav and
// vertical scroll slides it sideways (like "Where the value is hiding");
// on phones, or with reduced motion, it is a native swipe strip.
const WhyShowcase = ({ head }) => {
  const rootRef = useRef(null);
  const pinRef = useRef(null);
  const trackRef = useRef(null);
  const goToRef = useRef(() => {});
  const [active, setActive] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    const pin = pinRef.current;
    const sticky = pin.firstElementChild;
    const track = trackRef.current;
    const wide = window.matchMedia("(min-width: 768px)");
    const count = track.children.length;
    let enabled = false;
    let distance = 0;
    let pinTop = 0;
    let current = 0;
    let target = 0;
    let frame = null;
    let shown = -1;

    const show = (i) => {
      if (i !== shown) {
        shown = i;
        setActive(i);
      }
    };

    const render = () => {
      track.style.transform = `translate3d(${-current}px, 0, 0)`;
      show(Math.round((distance ? current / distance : 0) * (count - 1)));
    };

    const tick = () => {
      frame = null;
      current += (target - current) * 0.2;
      if (Math.abs(target - current) < 0.5) current = target;
      render();
      if (current !== target) frame = requestAnimationFrame(tick);
    };

    const readTarget = () => {
      const scrolled = pinTop - pin.getBoundingClientRect().top;
      target = Math.min(distance, Math.max(0, scrolled));
    };

    const onScroll = () => {
      if (!enabled) return;
      readTarget();
      if (frame === null) frame = requestAnimationFrame(tick);
    };

    // native swipe mode: follow the strip's own scroll position
    const onTrackScroll = () => {
      if (enabled) return;
      const slides = Array.from(track.children);
      const left = track.scrollLeft;
      let best = 0;
      slides.forEach((slide, i) => {
        if (
          Math.abs(slide.offsetLeft - slides[0].offsetLeft - left) <
          Math.abs(slides[best].offsetLeft - slides[0].offsetLeft - left)
        ) {
          best = i;
        }
      });
      show(best);
    };

    const measure = () => {
      enabled = wide.matches && !prefersReducedMotion();
      root.classList.toggle("is-hscroll", enabled);
      track.style.transform = "";
      pin.style.height = "";
      distance = 0;
      current = 0;
      target = 0;
      if (!enabled) return;
      pinTop = parseFloat(getComputedStyle(sticky).top) || 0;
      distance = Math.max(0, track.scrollWidth - track.clientWidth);
      pin.style.height = `${sticky.offsetHeight + distance}px`;
      readTarget();
      current = target;
      render();
    };

    goToRef.current = (i) => {
      const index = Math.max(0, Math.min(count - 1, i));
      if (enabled) {
        const offset = (index / (count - 1)) * distance;
        window.scrollTo({
          top: window.scrollY + pin.getBoundingClientRect().top - pinTop + offset,
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
      } else {
        const slides = Array.from(track.children);
        track.scrollTo({
          left: slides[index].offsetLeft - slides[0].offsetLeft,
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
      }
    };

    measure();
    const resizeObserver =
      "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(sticky);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    track.addEventListener("scroll", onTrackScroll, { passive: true });
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      track.removeEventListener("scroll", onTrackScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      root.classList.remove("is-hscroll");
    };
  }, []);

  const goTo = (i) => goToRef.current(i);

  return (
    <div className="hv2-showcase hv2-reveal" ref={rootRef}>
      <div className="hv2-showcase__pin" ref={pinRef}>
        <div className="hv2-showcase__sticky">
          {head}
          <div
            className="hv2-showcase__track"
            ref={trackRef}
            role="region"
            aria-label="Dsider decision dashboard views"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") goTo(active + 1);
              if (e.key === "ArrowLeft") goTo(active - 1);
            }}
          >
            {SHOWCASE.map((slide, i) => (
              <figure
                className={`hv2-showcase__slide${i === active ? " is-active" : ""}`}
                key={slide.label}
                aria-label={`${i + 1} of ${SHOWCASE.length}: ${slide.label}`}
              >
                <div className="hv2-wcard__head">
                  <span className="hv2-wcard__num">0{i + 1}</span>
                  <span className="hv2-wcard__tag">{slide.label}</span>
                </div>
                <h3>{slide.title}</h3>
                <div className="hv2-wcard__shot">
                  <img
                    src={slide.image}
                    alt={`Dsider decision dashboard, ${slide.label} view`}
                    draggable="false"
                  />
                </div>
                <p>{slide.text}</p>
              </figure>
            ))}
          </div>
          <div className="hv2-showcase__nav">
            <div className="hv2-showcase__dots">
              {SHOWCASE.map((slide, i) => (
                <button
                  type="button"
                  key={slide.label}
                  className={i === active ? "is-active" : ""}
                  aria-label={`Show ${slide.label} view`}
                  aria-current={i === active}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhySection = () => (
  <section className="hv2-section hv2-section--why">
    <div className="hv2-container">
      <WhyShowcase
        head={(
          <>
              <div className="hv2-why__head">
                <div className="hv2-why__intro">
                  <span className="hv2-eyebrow hv2-reveal">Why Dsider</span>
                  <h2 className="hv2-reveal" style={d(1)}>
                    One model, <em>from source to settlement.</em>
                  </h2>
                  <p className="hv2-lead hv2-reveal" style={d(2)}>
                    Dsider combines first-principles simulation with your contracts,
                    prices, and costs, so engineering, finance, and leadership work from
                    the same numbers.
                  </p>
                </div>
                <div className="hv2-soc hv2-reveal" style={d(2)}>
                  <img
                    src={`${IMG}/soc2-seal.png`}
                    alt="AICPA SOC 2"
                    width="126"
                    height="125"
                  />
                  <p>Dsider is SOC 2 Type 1 compliant.</p>
                </div>
              </div>
              <ol className="hv2-journey hv2-reveal" style={d(3)} aria-label="From wellhead to settlement">
                {JOURNEY.map((step, i) => (
                  <li key={step.label} className="hv2-journey__step" style={d(i)}>
                    <div className="hv2-journey__node">
                      <span className={`hv2-journey__dot hv2-journey__dot--${step.tone}`}>
                        <i />
                      </span>
                      <span className="hv2-journey__label">{step.label}</span>
                    </div>
                    {step.line && (
                      <span
                        className={`hv2-journey__line hv2-journey__line--${step.line}`}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                ))}
              </ol>
          </>
        )}
      />

      <div className="hv2-grid">
        {PILLARS.map((p, i) => (
          <article
            key={p.title}
            className="hv2-card hv2-card--dark hv2-reveal"
            style={d(i)}
          >
            <span className="hv2-card__icon">
              <img src={`${IMG}/${p.icon}`} alt="" width={p.width} height={p.height} />
            </span>
            <h3 style={{ maxWidth: p.titleWidth }}>{p.title}</h3>
            <p>{p.body}</p>
          </article>
        ))}
      </div>

    </div>
  </section>
);

const PathsSection = () => (
  <section
    id="choose-your-path"
    className="hv2-section hv2-section--paths"
  >
    <div className="hv2-container">
      <span className="hv2-eyebrow hv2-reveal">Choose Your Path</span>
      <h2 className="hv2-reveal" style={d(1)}>
        Start where <em>your decisions are.</em>
      </h2>
      <p className="hv2-lead hv2-reveal" style={d(2)}>
        Whether you move molecules, produce them, or are building what comes
        next, Dsider models the decisions in front of you. Pick the path that
        fits your work.
      </p>
      <div className="hv2-grid hv2-grid--paths">
        {PATHS.map((p, i) => (
          <article
            key={p.title}
            className="hv2-card hv2-card--path hv2-reveal"
            style={d(i + 1)}
          >
            <h3>{p.title}</h3>
            <div className={`hv2-shot hv2-shot--path ${p.imageClass || ""}`}>
              <img src={`${IMG}/${p.image}`} alt="" />
            </div>
            <p>{p.body}</p>
            {p.disabled ? (
              <button
                type="button"
                className="hv2-btn hv2-btn--ghost"
                disabled
                aria-disabled="true"
              >
                {p.cta}
              </button>
            ) : (
              <Link
                to={p.to}
                className={`hv2-btn ${p.primary ? "hv2-btn--primary" : "hv2-btn--ghost"}`}
              >
                {p.cta}
                {p.primary && <ArrowIcon />}
              </Link>
            )}
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ContactSection = ({ split = false }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (REQUIRED.includes(name)) {
      setErrors({ ...errors, [name]: validateField(name, value) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = REQUIRED.reduce((acc, key) => {
      const error = validateField(key, formData[key]);
      if (error) acc[key] = error;
      return acc;
    }, {});
    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      setStatus({ type: "", text: "" });
      const first = e.target.querySelector(`[name="${Object.keys(formErrors)[0]}"]`);
      if (first) first.focus();
      return;
    }

    const cleaned = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v.trim()])
    );
    setSending(true);
    try {
      // Industry has no dedicated HubSpot property yet, so it rides in the message.
      await submitToHubspot({
        ...cleaned,
        message: `Industry: ${cleaned.industry}`,
      });
      setStatus({ type: "success", text: "Thanks — we'll be in touch shortly." });
      setFormData(emptyForm);
      setErrors({});
    } catch (error) {
      console.error("Error making post request:", error.message);
      setStatus({
        type: "error",
        text: "There was a problem sending your request, please try again later.",
      });
    }
    setSending(false);
  };

  const field = (name, label, props = {}) => (
    <label className="hv2-field">
      <span>{label}</span>
      <input
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={100}
        required
        aria-invalid={errors[name] ? "true" : "false"}
        {...props}
      />
      {errors[name] && (
        <small className="hv2-field__error" role="alert">
          {errors[name]}
        </small>
      )}
    </label>
  );

  return (
    <section
      id="contact"
      className={`hv2-section hv2-section--cta${split ? " hv2-section--split" : ""}`}
    >
      <div className={`hv2-cta${split ? " hv2-cta--split" : ""}`}>
        {split ? (
          <div className="hv2-cta__copy">
            <span className="hv2-pill hv2-hero-in" style={d(0)}>
              <i aria-hidden="true" />
              Contact Dsider
            </span>
            <h1 className="hv2-hero-in" style={d(1)}>
              Let's
              <br />
              collaborate to
              <em>
                build a
                <br />
                sustainable
                <br />
                future
              </em>
            </h1>
          </div>
        ) : (
          <>
            <span className="hv2-eyebrow hv2-eyebrow--center hv2-reveal">Contact</span>
            <h2 className="hv2-reveal" style={d(1)}>Have a decision you'd like to see in dollars?</h2>
            <p className="hv2-cta__sub hv2-reveal" style={d(2)}>
              Tell us about it. We'll show you how Dsider would model it.
            </p>
          </>
        )}
        <form className="hv2-form hv2-reveal" style={d(3)} onSubmit={handleSubmit} noValidate>
          <div className="hv2-form__row">
            {field("firstName", "First Name *", {
              type: "text",
              placeholder: "First Name",
              autoComplete: "given-name",
            })}
            {field("lastName", "Last Name *", {
              type: "text",
              placeholder: "Last Name",
              autoComplete: "family-name",
            })}
          </div>
          <div className="hv2-form__row">
            {field("designation", "Title *", {
              type: "text",
              placeholder: "VP Operations, CFO, ...",
              autoComplete: "organization-title",
            })}
            {field("organization", "Company *", {
              type: "text",
              placeholder: "Your Company",
              autoComplete: "organization",
            })}
          </div>
          {field("email", "Work email *", {
            type: "email",
            placeholder: "you@company.com",
            autoComplete: "email",
          })}
          <label className="hv2-field">
            <span>Industry</span>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
            >
              {INDUSTRIES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          {status.text && (
            <p className={`hv2-form__status hv2-form__status--${status.type}`} role="status">
              {status.text}
            </p>
          )}
          <button
            type="submit"
            className="hv2-btn hv2-btn--primary hv2-btn--block"
            disabled={sending}
          >
            {sending ? "Sending..." : "Talk to our team"}
            {!sending && <ArrowIcon />}
          </button>
          <p className="hv2-form__alt">
            Or email <a href="mailto:info@dsider.app">info@dsider.app</a>
          </p>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="hv2-footer">
    <div className="hv2-container">
      <div className="hv2-footer__top">
        <div className="hv2-footer__brand">
          <Link to="/" aria-label="Dsider home">
            <img
              className="hv2-logo"
              src={`${IMG}/logo-footer.svg`}
              alt="Dsider"
              width="155"
              height="32"
            />
          </Link>
          <p>Decisions grounded in physics and economics.</p>
          <p>
            We're hiring. <Link to="/careers#openings">See open roles →</Link>
          </p>
        </div>
        <div className="hv2-footer__cols">
          <div>
            <h4>Contact</h4>
            <a href="mailto:contact@dsider.app">contact@dsider.app</a>
            <span>Houston, Texas</span>
          </div>
          <div>
            <h4>Navigation</h4>
            {FOOTER_NAV.map((l) => (
              <Link key={l.label} to={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="hv2-footer__bottom">
        <span>© 2026 Dsider Inc. All rights reserved.</span>
        <span>Dsider · Grounded in Physics and economics</span>
      </div>
    </div>
  </footer>
);

// Scroll to the element named by the URL hash (e.g. /careers#openings)
const useHashScroll = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el)
        el.scrollIntoView({
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);
};

// Reveal .hv2-reveal elements once as they scroll into view
const useReveal = (rootRef) => {
  useEffect(() => {
    const items = rootRef.current.querySelectorAll(".hv2-reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rootRef]);
};

// Shared page frame (nav, footer, reveal animations) for inner pages
export const PageShell = ({ title, description, children }) => {
  const rootRef = useRef(null);
  useReveal(rootRef);
  return (
    <div className="hv2" ref={rootRef}>
      <Helmet>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
      </Helmet>
      <TopNav />
      <main>{children}</main>
      <Footer />
    </div>
  );
};

// /contact: same nav, contact section and footer as the landing page
export const ContactPage = () => {
  const rootRef = useRef(null);
  useReveal(rootRef);
  return (
    <div className="hv2" ref={rootRef}>
      <Helmet>
        <title>Contact | Dsider</title>
        <meta
          name="description"
          content="Talk to the Dsider team about decisions grounded in physics and economics."
        />
      </Helmet>
      <TopNav />
      <main>
        <ContactSection split />
      </main>
      <Footer />
    </div>
  );
};

const OPENINGS = [
  {
    id: "Dsr0027",
    title: "Data Analytics Engineer",
    location: "United States, Remote",
    level: "Intermediate Level (3+ years)",
    postedOn: "August 21, 2026",
    // role page exists
    to: "/Careers/Jobs/Dsr0027",
  },
  {
    id: "product-manager",
    title: "Product Manager",
    location: "United States, Remote",
    level: "Intermediate Level (3+ years)",
    postedOn: "August 21, 2026",
    // no role page yet, so Apply stays disabled
    to: null,
  },
];

const PERKS = [
  {
    title: "Ideas that ship",
    body: "We're building something new, so good ideas get tested, not tabled. Bring your curiosity and your point of view, and expect to see your work in the product.",
  },
  {
    title: "Grow with us",
    body: "Your career goals shape the work you take on. We back your development with real opportunities to stretch, learn, and lead as the company grows.",
  },
  {
    title: "Better together",
    body: "Engineers, designers, and domain experts work side by side with a shared mission. Here, the best answer wins, whoever it comes from.",
  },
];

// /careers: same nav and footer as the landing page
export const CareersPage = () => {
  const rootRef = useRef(null);
  useReveal(rootRef);
  useHashScroll();
  return (
    <div className="hv2" ref={rootRef}>
      <Helmet>
        <title>Careers | Dsider</title>
        <meta
          name="description"
          content="Join Dsider and help build decision intelligence for the energy industry."
        />
      </Helmet>
      <TopNav />
      <main>
        <section className="hv2-careers-hero">
          <div className="hv2-careers-hero__inner">
            <div className="hv2-careers-hero__copy">
              <span className="hv2-pill hv2-hero-in" style={d(0)}>
                <i aria-hidden="true" />
                Careers
              </span>
              <h1 className="hv2-hero-in" style={d(1)}>
                Help energy teams see <em>every decision in dollars.</em>
              </h1>
              <p className="hv2-careers-hero__sub hv2-hero-in" style={d(2)}>
                We're building decision intelligence grounded in physics and
                economics. Join a small team working on problems that move real
                money.
              </p>
              <div className="hv2-hero__actions hv2-hero-in" style={d(3)}>
                <Link to="/careers#openings" className="hv2-btn hv2-btn--primary">
                  See Open Roles <ArrowIcon />
                </Link>
              </div>
            </div>
            <div className="hv2-careers-hero__visual hv2-hero-in hv2-from-right" style={d(2)}>
              <img
                src={`${IMG}/careers-offshore.png`}
                alt="Isometric illustration of an offshore platform and vessel"
              />
            </div>
          </div>
        </section>

        <section className="hv2-section hv2-careers-perks">
          <div className="hv2-container">
            <span className="hv2-eyebrow hv2-reveal">Why Join Dsider</span>
            <h2 className="hv2-reveal" style={d(1)}>
              Do the work<br />
              <em>you’ll be proud to defend.</em>
            </h2>
            <p className="hv2-careers-perks__lead hv2-reveal" style={d(2)}>
              We’re a small team turning physics and economics into decisions
              energy companies can trust. If you want your ideas to shape a
              product, and your growth to be taken seriously, this is the place.
            </p>
            <div className="hv2-perks">
              {PERKS.map((p, i) => (
                <div className="hv2-card hv2-reveal" key={p.title} style={d(i + 2)}>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="openings" className="hv2-section hv2-careers-openings">
          <div className="hv2-container">
            <span className="hv2-eyebrow hv2-reveal">Choose Your Path</span>
            <h2 className="hv2-reveal" style={d(1)}>
              Start where <em>your decisions are.</em>
            </h2>
            <p className="hv2-careers-perks__lead hv2-reveal" style={d(2)}>
              Whether you move molecules, produce them, or are building what
              comes next, Dsider models the decisions in front of you. Pick the
              path that fits your work.
            </p>
            <div className="hv2-jobs">
              {OPENINGS.map((job, i) => (
                <div
                  key={job.id}
                  className="hv2-job hv2-reveal"
                  style={d(i + 3)}
                >
                  <div className="hv2-job__main">
                    <h3>{job.title}</h3>
                    <div className="hv2-job__meta hv2-job__meta--labeled">
                      <span>
                        <b>Location :</b> {job.location}
                      </span>
                      <span>
                        <b>Position:</b> {job.level}
                      </span>
                      <span>
                        <b>Posted on:</b> {job.postedOn}
                      </span>
                    </div>
                  </div>
                  {job.to ? (
                    <Link to={job.to} className="hv2-btn hv2-btn--primary hv2-btn--sm">
                      Apply
                    </Link>
                  ) : (
                    <span
                      className="hv2-btn hv2-btn--primary hv2-btn--sm is-disabled"
                      aria-disabled="true"
                    >
                      Apply
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const HomeV2 = () => {
  const rootRef = useRef(null);

  useReveal(rootRef);

  useHashScroll();

  return (
  <div className="hv2" ref={rootRef}>
    <Helmet>
      <title>Dsider | Decisions Grounded in Physics and Economics</title>
      <meta
        name="description"
        content="Dsider follows the molecule from source to settlement and puts a dollar value on every decision along the way."
      />
    </Helmet>
    <TopNav />
    <main>
      <Hero />
      <WhySection />
      <PathsSection />
      <ContactSection />
    </main>
    <Footer />
  </div>
  );
};

export default HomeV2;
